import { useLayoutEffect, useRef, useState } from 'react';
import type { UsAccountContext } from '@/lib/polymarketUs/accountClient';
import type { UsSelectedMarket } from '@/lib/polymarketUs/marketClient';
import type { InspectInstitutionalDraft, InstitutionalDraftInspection } from '@/lib/polymarketUs/institutionalDraftClient';
import { buildDraft, checkInstitutionalEntry, isClean, type UsTicketEntry } from '@/lib/polymarketUs/ticketModel';
import { usEvidenceNow } from '@/lib/polymarketUs/timeEvidence';
import type { UsTarget } from '@/lib/polymarketUs/types';
import { UsTicketNotice } from './UsTicketNotice';
import type { UsHostMarketReadiness } from './UsManualTicket';

/** Same draft and button position; no executable preview, submit or recovery state. */
export function InstitutionalDraftCheck({ context, entry, selected, target, hostIdentity, hostReadiness, blocked, inspector, now, clock, className }: {
  context: UsAccountContext; entry: UsTicketEntry; selected: UsSelectedMarket; target: UsTarget;
  hostIdentity: string; hostReadiness?: UsHostMarketReadiness; blocked: boolean; inspector?: InspectInstitutionalDraft; now: number; clock(): number; className: string;
}) {
  const identity = JSON.stringify([context.accountId, context.scope, context.protocol, context.institutional,
    context.accountEnabled, context.scopeUse, context.permissions, context.readiness,
    entry, target, selected.rules.revision, selected.feedGeneration, selected.market.classification.revision,
    hostReadiness?.state ?? selected.market.book.state, hostIdentity, blocked]);
  const [previous, setPrevious] = useState({ identity, inspector });
  const live = useRef(identity);
  const latest = useRef({ selected, hostReadiness });
  useLayoutEffect(() => { latest.current = { selected, hostReadiness }; });
  const request = useRef<AbortController | null>(null);
  const count = useRef(0);
  const [state, setState] = useState<{ identity: string; pending?: boolean; result?: InstitutionalDraftInspection; failed?: boolean } | null>(null);
  if (previous.identity !== identity || previous.inspector !== inspector) { setPrevious({ identity, inspector }); setState(null); }
  useLayoutEffect(() => {
    live.current = identity;
    request.current?.abort(); request.current = null;
    return () => { live.current = ''; request.current?.abort(); request.current = null; };
  }, [identity, inspector]);
  const current = state?.identity === identity ? state : null;
  const evidenceNow = usEvidenceNow(selected.timeEvidence, now);
  const fresh = (at: number, snapshot: { selected: UsSelectedMarket; hostReadiness?: UsHostMarketReadiness }) => {
    const { selected: current, hostReadiness: host } = snapshot;
    const evidenceAt = usEvidenceNow(current.timeEvidence, at), book = current.market.book;
    const bookCurrent = host ? host.state === 'current' && host.expiresAtMs > usEvidenceNow(host.timeEvidence, at)
      : book.state === 'current' && book.expiresAtMs !== null && book.expiresAtMs > evidenceAt;
    return Number.isFinite(evidenceAt) && current.rules.active && current.rules.expiresAtMs > evidenceAt
      && current.market.classification.expiresAtMs > evidenceAt && bookCurrent;
  };
  const allowed = !blocked && !!inspector && fresh(now, { selected, hostReadiness }) && isClean(checkInstitutionalEntry(entry, selected.rules, context, evidenceNow));
  const result = current?.result;
  const expired = !!result && (!fresh(now, { selected, hostReadiness }) || usEvidenceNow(result.timeEvidence, now) >= result.expiresAtMs);
  async function inspect() {
    if (!allowed || !inspector || request.current || !fresh(clock(), latest.current)) return;
    const controller = new AbortController(); request.current = controller;
    const mine = identity;
    const draft = buildDraft(entry, context.scope, target, `inspection-${++count.current}`);
    setState({ identity: mine, pending: true });
    const timer = setTimeout(() => {
      if (request.current !== controller || live.current !== mine) return;
      controller.abort();
      if (request.current === controller) request.current = null;
      if (live.current === mine) setState({ identity: mine, failed: true });
    }, 25_000);
    try {
      const answer = await inspector(context.accountId, draft, controller.signal);
      if (controller.signal.aborted || request.current !== controller || live.current !== mine || !fresh(clock(), latest.current)) return;
      if (usEvidenceNow(answer.timeEvidence, clock()) >= answer.expiresAtMs) throw new Error('Expired inspection');
      setState({ identity: mine, result: answer });
    } catch {
      if (!controller.signal.aborted && request.current === controller && live.current === mine) setState({ identity: mine, failed: true });
    } finally {
      clearTimeout(timer);
      if (request.current === controller) {
        request.current = null;
        setState(old => old?.identity === mine && old.pending ? null : old);
      }
    }
  }
  return <>
    <button type="button" data-testid="us-ticket-check-draft" className={className}
      disabled={!allowed || !!current?.pending} onClick={() => void inspect()}
      title="Checks this draft with the institutional venue. Does not place an order.">
      {current?.pending ? 'Checking draft…' : 'Check draft'}
    </button>
    {(result || current?.failed) && <UsTicketNotice testId="us-ticket-inspection" className="mt-2"
      warning={!!current?.failed || expired || result?.mappingState === 'candidate_unconfirmed'}
      summary={current?.failed ? 'Draft check unavailable. No order was sent.' : expired
        ? 'Draft check expired. No order was sent.' : 'Draft checked · no order sent.'}>
      {result && <p>Checked {new Date(result.observedAtMs).toLocaleTimeString()}. {result.mappingState === 'candidate_unconfirmed'
        ? 'The native symbol is a candidate; its market and outcome mapping is unconfirmed.'
        : 'The supplied structured mapping matched this check.'} Fees and account effects remain unconfirmed. Trading remains unavailable.</p>}
      {current?.failed && <p>Account access, venue configuration or current market evidence prevented this check. Your draft is kept.</p>}
    </UsTicketNotice>}
  </>;
}
